import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { inject, observer } from "mobx-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  useDeleteV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberResellerResellerIdMutation,
  usePostV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberResellerResellerIdEmailMutation
} from "randmar-api-client";
import { Avatar, AvatarFallback, AvatarImage, Button, Card, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, useToast } from "randmarcomps";
import { Landmark, Loader2, MailIcon, Send } from "lucide-react";
import ButtonWithActions from '../../components/util/ButtonWithActions';
import withManufacturer from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import Reseller from '../../stores/reseller/Reseller';
import { RootStore } from "../../stores/rootStore";
import ResellerActions from "../reseller/ResellerActions";
import ButtonWithDialogConfirm from "../util/ButtonWithDialogConfirm";

interface IProps {
  store?: RootStore;
  resellers?: Reseller[];
  applicationId: string;
  bidNumber: string;
}

const BidResellersGrid: React.FC<IProps> = (props) => {
  const { applicationId, resellers, bidNumber, store } = props;
  const { userStore, opportunityStore } = store!;
  const { toast } = useToast();

  const [removingResellerId, setRemovingResellerId] = useState<string | null>(null);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [selectedReseller, setSelectedReseller] = useState<Reseller>();
  const [email, setEmail] = useState(userStore.user?.profile?.email ?? "");
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const [sendEmailMutation] = usePostV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberResellerResellerIdEmailMutation();
  const [removeResellerMutation] = useDeleteV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberResellerResellerIdMutation();

  const isSuperAdmin = userStore.isSuperAdmin();

  const handleConfirm = (resellerId: string) => {
    setRemovingResellerId(resellerId);
    const successMessage = `${resellerId} removed from ${bidNumber}`;
    const failMessage = `${resellerId} could not be removed from ${bidNumber}`;

    removeResellerMutation({
      routeManufacturerId: applicationId,
      bidNumber: bidNumber,
      resellerId: resellerId
    }).unwrap()
      .then(() => {
        toast({ variant: "success", title: "Success", description: successMessage });
        opportunityStore.getOpportunity(applicationId, bidNumber, true);
      })
      .catch(() => {
        toast({ variant: "destructive", title: "Error", description: failMessage });
      })
      .finally(() => {
        setRemovingResellerId(null);
      });
  };

  const sendResellerBidPricingEmail = () => {
    if (!selectedReseller) return;
    setIsSendingEmail(true);
    sendEmailMutation({
      routeManufacturerId: applicationId,
      bidNumber: bidNumber,
      resellerId: selectedReseller.resellerId ?? "",
      emailAddress: email
    }).then(_response => {
      toast({ variant: "success", title: "Success", description: `Bid pricing sent to ${email}` });
      setIsEmailDialogOpen(false);
    }).catch(_error => {
      toast({ variant: "destructive", title: "Error", description: "Failed to send bid pricing." });
    }).finally(() => {
      setIsSendingEmail(false);
    });
  };

  return (
    <Card className="w-full">
      <div className="w-full overflow-auto rounded-[20px]">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ borderColor: "#582c83" }} />
              <TableCell align="center" sx={{ borderColor: "#582c83" }} />
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Name</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Location</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Phone</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }} />
              {isSuperAdmin && <TableCell align="center" sx={{ borderColor: "#582c83" }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {(resellers || [])
              .slice()
              .map((row) => (
                <TableRow sx={{ "&:hover": { backgroundColor: row.onHold ? "#f6e4e4" : "#f9f5ff" } }} key={row.resellerId}>
                  <TableCell align="center">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Landmark className={row.onHold ? "text-red-500" : "text-green-500"} />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{row.onHold ? "On hold" : "Not on hold"}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell align="center">
                    <Link to={Path.reseller(applicationId, row.resellerId)}>
                      <Avatar className="h-[30px] w-[30px] border border-gray-300">
                        <AvatarImage
                          alt={row.name ?? ""}
                          src={`https://api.randmar.io/v4/Partner/${row.resellerId}/Account/Logo`}
                        />
                        <AvatarFallback>{row.name?.slice(0, 2)}</AvatarFallback>
                      </Avatar>
                    </Link>
                  </TableCell>
                  <TableCell align="center">
                    <Link to={Path.reseller(applicationId, row.resellerId)}>{row.name}</Link>
                  </TableCell>
                  <TableCell align="center">{row.city}, {row.province}, {row.country}</TableCell>
                  <TableCell align="center">{row.phone}</TableCell>
                  <TableCell align="center">
                    <div className="flex items-center gap-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon" variant="outline" className="text-primary border-primary"
                              onClick={() => {
                                setSelectedReseller(row);
                                setIsEmailDialogOpen(true);
                              }}
                            >
                              <MailIcon />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Send Bid Pricing</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <ButtonWithActions buttonText="Actions" backgroundColor="#f1f2f4" color="#363636">
                        <ResellerActions reseller={row} />
                      </ButtonWithActions>
                    </div>
                  </TableCell>
                  {isSuperAdmin &&
                    <TableCell align="center">
                      {removingResellerId === row.resellerId ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <ButtonWithDialogConfirm
                          fab={true}
                          onConfirmation={() => handleConfirm(row.resellerId!)}
                          buttonTitle={`Remove ${row.name} from ${bidNumber}`}
                        />
                      )}
                    </TableCell>}
                </TableRow>
              ))}
          </TableBody>
        </Table>

        <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Send Reseller Bid Pricing</DialogTitle>
              <DialogDescription>
                The Reseller Bid Pricing will be sent to the recipient's email address below.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="email" className="text-right">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="col-span-3"
                  placeholder="recipient@example.com"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="submit"
                onClick={sendResellerBidPricingEmail}
                disabled={isSendingEmail || !email}
              >
                {isSendingEmail ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Card>
  );
};

// Wrap the component with HOCs to inject manufacturer context and MobX store, and to make it reactive.
export default withManufacturer(inject("store")(observer(BidResellersGrid)));
